import { useEffect, useState } from 'react';
import { X } from '@phosphor-icons/react';
import { AhaTicketGeneratorPrompt, promptAnalyzer } from '@Models/types';
import { analyzerAPI } from '@Lib/axios/analyzer';
import showToast from '@Common/toast/showToast';
import { useAuth } from '@Context/CognitoAuthProvider';
import { promptToolboxAPI } from '@Lib/axios/prompt-toolbox';
import { IoIosWarning } from 'react-icons/io';
interface ModalProps {
  isOpen: boolean;
  onCancel: () => void;
  prompt: promptAnalyzer | AhaTicketGeneratorPrompt | null;
  onPromptEdited?: (prompts: promptAnalyzer[]) => void;
  type?: string;
}

const ViewPromptModal = ({
  isOpen,
  onCancel,
  prompt,
  onPromptEdited,
  type,
}: ModalProps) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(prompt ? prompt.prompt : '');
  const { user, logout } = useAuth();
  const analyzer = analyzerAPI(logout);
  const promptToolBox = promptToolboxAPI(logout);

  useEffect(() => {
    console.log('ViewPromptModal prompt prop:', prompt);
  }, [prompt]);

  useEffect(() => {
    if (user?.admin) {
      setIsAdmin(true);
    } else {
      setIsAdmin(false);
    }
  }, []);

  useEffect(() => {
    setEditedText(prompt ? prompt.prompt : '');
  }, [prompt]);

  const handleEditPrompt = async () => {
    try {
      let updatedPrompts: promptAnalyzer[] | AhaTicketGeneratorPrompt[];
      if (type === 'generator prompt') {
        await promptToolBox.editAhaTicketGeneratorPrompt({
          ...prompt!,
          prompt: editedText,
        });
        showToast.success('Prompt updated successfully!');
        setIsEditing(false);
        updatedPrompts = await promptToolBox.getAhaTicketGeneratorPrompts();
      } else {
        await analyzer.updateAnalyzerPrompt({
          ...prompt!,
          prompt: editedText,
        });
        showToast.success('Prompt updated successfully!');
        setIsEditing(false);
        updatedPrompts = await analyzer.getAnalyzerPrompts();
      }
      if (onPromptEdited) {
        onPromptEdited(updatedPrompts);
      }
    } catch (error) {
      console.error(error);
      showToast.error('Error updating prompt');
    }
  };
  return (
    <div
      onClick={() => {
        if (isEditing) setIsEditing(false);
      }}
      className={`${
        isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
      } fixed inset-0 z-50 flex h-full w-full items-center justify-center bg-gray-100 bg-opacity-70 transition-opacity duration-300`}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`flex transform flex-col rounded-xl shadow-xl transition-transform duration-300 md:w-1/2 ${
          isOpen ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}
      >
        <div className="flex h-12 items-center justify-between rounded-tl-lg rounded-tr-lg bg-primary-700 px-4 py-3">
          <p className="flex-[1.3] font-semibold text-white">
            {prompt ? prompt.name : 'Default Prompt'}
          </p>
          <div className="flex-1">
            <div className="flex flex-1 justify-end gap-2">
              <X
                onClick={() => {
                  setIsEditing(false);
                  onCancel();
                }}
                size={20}
                className="cursor-pointer text-white hover:text-red-500"
              />
            </div>
          </div>
        </div>
        <div className="relative flex h-80 flex-col gap-2 rounded-bl-lg rounded-br-lg bg-white p-4 font-inter text-primary-700">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-semibold">
              Author: {prompt ? prompt.author : 'N/A'}
            </p>
          </div>
          <textarea
            onChange={(e) => setEditedText(e.target.value)}
            readOnly={!(isAdmin && isEditing)}
            value={editedText}
            className="custom-scroll h-full w-full resize-none rounded-lg border-2 px-4 py-1 outline-primary-700 placeholder:italic placeholder:text-gray-400"
          />
          {isAdmin && isEditing && (
            <div className="flex items-center gap-2 self-end">
              <button
                onClick={() => setIsEditing(false)}
                className="hover:bg-primary-800 active:bg-primary-900 bg-gray-200 px-4 py-2 text-black transition duration-200 ease-in-out hover:scale-105 hover:opacity-100 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary-400 active:scale-95"
              >
                Cancel
              </button>
              <button
                onClick={handleEditPrompt}
                className="hover:bg-primary-800 active:bg-primary-90 rounded bg-primary-700 px-4 py-2 text-white transition duration-200 ease-in-out hover:scale-105 hover:opacity-100 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary-400 active:scale-95"
              >
                Save
              </button>
            </div>
          )}
          {isAdmin && !isEditing && (
            <div className="flex items-center gap-2 self-end">
              <button
                onClick={() => setIsEditing(true)}
                className="hover:bg-primary-800 active:bg-primary-900 rounded bg-primary-700 px-4 py-2 text-white transition duration-200 ease-in-out hover:scale-105 hover:opacity-100 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary-400 active:scale-95"
              >
                Edit
              </button>
            </div>
          )}
        </div>
        {type === 'generator prompt' ? (
          <div className="flex flex-col rounded-b-lg bg-white px-11 pb-5 text-red-600">
            <span className="flex flex-row items-center gap-1 text-xs font-bold sm:text-base">
              <IoIosWarning className="text-red-600" size="1rem" />
              Note:
            </span>
            <span className="mt-2 text-justify text-xs font-semibold sm:text-sm">
              The prompt you enter will be appended to a pre-determined
              instruction and format to ensure consistency in the generated
              feature ticket for the Aha! API. Please make sure to include the{' '}
              <span className="font-bold">{'{prompt} '}</span>
              placeholder where the feature description should go.
            </span>
          </div>
        ) : (
          <div></div>
        )}
      </div>
    </div>
  );
};

export default ViewPromptModal;
